<script setup lang="ts">
import { PhCopy } from '@phosphor-icons/vue'
import { computed, shallowRef, watch } from 'vue'
import { useToast } from '@/composables/useToast'
import { buildImageResizeUrl } from '@/utils/distribute/media-url'
import type { Props } from './type'

defineOptions({
  name: 'ProfileUserHeader',
})

const props = defineProps<Props>()

const toast = useToast()
const summaryLines = computed(() =>
  (props.profile.setting?.summary ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean),
)
const avatarFrame = computed(() => props.profile.setting?.avatarFrame ?? '')
const clonedAvatar = computed(() => props.profile.cloned?.avatar ?? '')
const avatarSrc = computed(
  () => buildImageResizeUrl(props.profile.avatar, { width: 360, height: 360, mode: 'fill' }) ?? props.profile.avatar,
)
const clonedAvatarSrc = computed(
  () =>
    buildImageResizeUrl(clonedAvatar.value, { width: 168, height: 168, mode: 'fill' }) ??
    clonedAvatar.value,
)
const avatarLoaded = shallowRef(false)
const clonedAvatarLoaded = shallowRef(false)

watch(
  () => avatarSrc.value,
  () => {
    avatarLoaded.value = false
  },
  { immediate: true },
)

watch(
  () => clonedAvatarSrc.value,
  () => {
    clonedAvatarLoaded.value = false
  },
  { immediate: true },
)

async function copyUid() {
  if (!props.profile.uid) {
    return
  }

  try {
    await navigator.clipboard?.writeText(props.profile.uid)
    toast.success('已复制 UID')
  } catch {
    toast.info(props.profile.uid)
  }
}

function handleAvatarLoad() {
  avatarLoaded.value = true
}

function handleClonedAvatarLoad() {
  clonedAvatarLoaded.value = true
}
</script>

<template>
  <header class="profile-header">
    <div class="profile-header__avatar-group">
      <div class="profile-header__avatar-shell">
        <img
          class="profile-header__avatar"
          :class="{ 'is-loaded': avatarLoaded }"
          :src="avatarSrc"
          :alt="profile.nickname"
          @load="handleAvatarLoad"
        />
      </div>
      <img
        v-if="avatarFrame"
        class="profile-header__avatar-frame"
        :src="avatarFrame"
        alt=""
        aria-hidden="true"
      />
      <img
        v-if="clonedAvatarSrc"
        class="profile-header__clone"
        :class="{ 'is-loaded': clonedAvatarLoaded }"
        :src="clonedAvatarSrc"
        :alt="`${profile.nickname}的分身`"
        @load="handleClonedAvatarLoad"
      />
    </div>

    <div class="profile-header__identity">
      <h1 class="profile-header__name">{{ profile.nickname }}</h1>
      <button type="button" class="profile-header__uid" @click="copyUid">
        <span>{{ profile.uid }}</span>
        <PhCopy :size="14" />
      </button>
    </div>

    <p v-if="summaryLines.length === 0" class="profile-header__summary is-empty">还没有个人简介</p>
    <div v-else class="profile-header__summary">
      <p v-for="line in summaryLines" :key="line">{{ line }}</p>
    </div>
  </header>
</template>

<style scoped>
.profile-header {
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px 22px;
  color: var(--profile-text);
  text-align: center;
}

.profile-header__avatar-group {
  position: relative;
  width: 180px;
  height: 180px;
}

.profile-header__avatar-shell,
.profile-header__avatar {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120px;
  height: 120px;
  transform: translate(-50%, -50%);
}

.profile-header__avatar-shell {
  border-radius: 50%;
  overflow: hidden;
  background: var(--app-skeleton-base);
}

.profile-header__avatar {
  border-radius: 50%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 0.22s ease;
}

.profile-header__avatar.is-loaded {
  opacity: 1;
}

.profile-header__avatar-frame {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 180px;
  height: 180px;
  object-fit: contain;
  pointer-events: none;
  transform: translate(-50%, -50%);
}

.profile-header__clone {
  position: absolute;
  top: 42px;
  right: 6px;
  width: 56px;
  height: 56px;
  border: 2px solid var(--profile-bg);
  border-radius: 50%;
  background: var(--app-skeleton-base);
  object-fit: cover;
  overflow: hidden;
  opacity: 0;
  transition: opacity 0.22s ease;
}

.profile-header__clone.is-loaded {
  opacity: 1;
}

.profile-header__identity {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  margin-top: -2px;
}

.profile-header__name {
  margin: 0;
  color: var(--profile-text);
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
}

.profile-header__uid {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--profile-muted);
  font-size: 14px;
  line-height: 20px;
}

.profile-header__uid:hover {
  color: var(--profile-text);
}

.profile-header__summary {
  max-width: 560px;
  margin-top: 14px;
  color: var(--profile-text);
  font-size: 14px;
  line-height: 21px;
}

.profile-header__summary p {
  margin: 0;
}

.profile-header__summary.is-empty {
  color: var(--profile-muted);
}
@media (max-width: 640px) {
  .profile-header {
    padding: 28px 16px 18px;
  }

  .profile-header__avatar-group {
    width: 150px;
    height: 150px;
  }

  .profile-header__avatar-shell,
  .profile-header__avatar {
    width: 100px;
    height: 100px;
  }

  .profile-header__avatar-frame {
    width: 150px;
    height: 150px;
  }

  .profile-header__clone {
    top: 36px;
    right: 4px;
    width: 48px;
    height: 48px;
  }

  .profile-header__name {
    font-size: 22px;
    line-height: 30px;
  }

  .profile-header__summary {
    font-size: 13px;
    line-height: 20px;
  }
}
</style>
